<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <span class="title">{{ title }}</span>
        <div class="formula-input" @click="focusInput">
          <span v-for="(part, index) in formulaParts" :key="index">
            <span v-if="!part.isChip" class="formula-text">{{
              part.text
            }}</span>
            <v-chip
              v-else
              outlined
              color="primary"
              small
              close
              @click:close="removeChip(index)"
            >
              {{ part.text }}
            </v-chip>
          </span>
          <input
            ref="input"
            v-model="currentInput"
            type="text"
            hint="Insira a fórmula, ao finalizar pressione Enter ou Espaço"
            @keyup.enter="addCurrentInput"
            @keyup.space="addCurrentInput"
            @keydown.backspace="handleBackspace"
          />
        </div>
        <span class="caption">{{ caption }}</span>
      </v-col>

      <v-col cols="12">
        <v-chip-group>
          <v-chip
            v-for="item in fields"
            :key="item.name"
            outlined
            color="secondary"
            @click="addChip(item)"
          >
            {{ item.label }}
          </v-chip>
        </v-chip-group>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { LOAD_ALL_FIELDS } from '../../../../vuex/modules/structure'

const NUMERIC_KEYBOARD_TYPES = ['numeric', 'number-pad', 'decimal-pad']

const TIME_OPERATORS = ['+', '-', '(', ')']

const isNumericInput = field =>
  NUMERIC_KEYBOARD_TYPES.includes(field.type) || field.mask === 'numberReal'

const isTimeInput = field =>
  field.componentType === 'timePiker' || field.mask === 'minute'

const isNumericFormulaInput = field =>
  field.componentType === 'text' ||
  (field.componentType === 'inputText' && isNumericInput(field))

export default {
  name: 'Calculate',

  props: {
    value: {
      type: String,
      default: '',
    },

    timeOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      formulaParts: [],
      currentInput: '',
      fields: [],
    }
  },

  computed: {
    title() {
      return this.timeOnly
        ? 'Cálculo de tempo, insira a regra abaixo.'
        : 'Campo calculado, insira a regra abaixo.'
    },

    caption() {
      return this.timeOnly
        ? 'Use apenas campos de tempo, + , - e parênteses. Ao finalizar pressione Enter ou Espaço.'
        : 'Insira a fórmula, ao finalizar pressione Enter ou Espaço.'
    },
  },

  watch: {
    formulaParts: {
      deep: true,
      handler() {
        const formulate = this.createFormula()
        this.$emit('input', formulate)
      },
    },
  },

  created() {
    this.getFields().then(() => {
      if (this.value) {
        // Regex sem a barra invertida no hífen para atender ao ESLint
        const parts = this.value
          .replace(/\{(@\w+)\}|[()/*+-]/g, ' $& ')
          .trim()
          .split(/\s+/)

        this.formulaParts = parts.map(part => {
          if (part.startsWith('{@')) {
            const key = part.slice(2, -1)
            const field = this.fields.find(f => f.name === key)

            return {
              isChip: true,
              text: field ? field.label : key,
              key,
            }
          }

          return { isChip: false, text: part }
        })
      }
    })
  },

  methods: {
    ...mapActions('structure', [LOAD_ALL_FIELDS]),

    async getFields() {
      const fields = await this.LOAD_ALL_FIELDS()

      this.fields = fields.filter(
        this.timeOnly ? isTimeInput : isNumericFormulaInput,
      )
    },

    focusInput() {
      this.$refs.input.focus()
    },

    addChip(item) {
      this.addCurrentInput()
      this.formulaParts.push({ isChip: true, text: item.label, key: item.name })
      this.focusInput()
    },

    addCurrentInput() {
      const trimmed = this.currentInput.trim()
      if (trimmed) {
        // Regex sem a barra invertida no hífen para atender ao ESLint
        const tokens = trimmed
          .replace(/\{(@\w+)\}|[()/*+-]/g, ' $& ')
          .trim()
          .split(/\s+/)

        tokens
          .filter(token => !this.timeOnly || TIME_OPERATORS.includes(token))
          .forEach(token => {
            this.formulaParts.push({ isChip: false, text: token })
          })

        this.currentInput = ''
      }
    },

    removeChip(index) {
      this.formulaParts.splice(index, 1)
    },

    handleBackspace() {
      if (this.currentInput === '' && this.formulaParts.length > 0) {
        this.formulaParts.pop()
      }
    },

    createFormula() {
      if (this.formulaParts.length === 0) {
        return ''
      }

      return this.formulaParts
        .map(part => (part.isChip ? `{@${part.key}}` : part.text))
        .join('')
    },
  },
}
</script>

<style scoped>
.formula-input {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  border: 1px solid #ccc;
  padding: 8px 12px;
  border-radius: 4px;
  min-height: 56px;
  cursor: text;
}
.formula-input input {
  border: none;
  outline: none;
  flex: 1;
  min-width: 120px;
}
.formula-text {
  padding: 0 2px;
  font-weight: 500;
}
</style>
